import { DivIcon } from "leaflet";
import { Coordinates } from "./types/MapData";

export const roundValue = (val: number, power = 4) => {
  const pow = Math.pow(10, power);
  return Math.round(val * pow) / pow;
};

/**
 * ポリゴンの重心を計算する（単純平均）
 * @param coordinates 座標の配列
 * @returns 重心の座標、座標が空の場合はundefined
 */
export const calcGravityCenter = (coordinates: Coordinates[]): Coordinates | undefined => {
  if (coordinates.length === 0) {
    return undefined;
  }

  // 各座標の平均を計算
  const sum = coordinates.reduce(
    (acc, coord) => ({
      x: acc.x + coord.x,
      y: acc.y + coord.y,
    }),
    { x: 0, y: 0 },
  );

  return {
    x: sum.x / coordinates.length,
    y: sum.y / coordinates.length,
  };
};

/**
 * ポリゴンの重心を計算する（面積を考慮した正確な計算）
 * @param coordinates 座標の配列（3つ以上のポリゴン）
 * @returns 重心の座標、座標が3未満の場合は単純平均を返す
 */
export const calcPolygonCentroid = (coordinates: Coordinates[]): Coordinates | undefined => {
  if (coordinates.length === 0) {
    return undefined;
  }

  if (coordinates.length < 3) {
    // ポリゴンとして扱えない場合は単純平均
    return calcGravityCenter(coordinates);
  }

  let area = 0;
  let cx = 0;
  let cy = 0;

  // ポリゴンの符号付き面積と重心を計算
  for (let i = 0; i < coordinates.length; i++) {
    const j = (i + 1) % coordinates.length;
    const xi = coordinates[i].x;
    const yi = coordinates[i].y;
    const xj = coordinates[j].x;
    const yj = coordinates[j].y;

    const cross = xi * yj - xj * yi;
    area += cross;
    cx += (xi + xj) * cross;
    cy += (yi + yj) * cross;
  }

  area /= 2;

  // 面積がゼロの場合（すべての点が一直線上にある場合）は単純平均
  if (Math.abs(area) < 1e-10) {
    return calcGravityCenter(coordinates);
  }

  return {
    x: cx / (6 * area),
    y: cy / (6 * area),
  };
};

const isPointOnSegment = (point: Coordinates, start: Coordinates, end: Coordinates) => {
  const epsilon = 1e-10;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const squaredLength = dx ** 2 + dy ** 2;

  // 同一点が連続するゼロ長線分は、その点と一致するときのみ辺上とみなす。
  if (squaredLength <= epsilon) {
    return Math.abs(point.x - start.x) <= epsilon && Math.abs(point.y - start.y) <= epsilon;
  }

  const cross = (point.y - start.y) * (end.x - start.x) - (point.x - start.x) * (end.y - start.y);
  if (Math.abs(cross) > epsilon) return false;

  const dot = (point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y);
  if (dot < 0) return false;

  return dot <= squaredLength;
};

export const isPointInPolygon = (point: Coordinates, coords: Coordinates[]) => {
  if (coords.length < 3) {
    return false;
  }

  let isInside = false;

  for (let index = 0; index < coords.length; index += 1) {
    const current = coords[index];
    const next = coords[(index + 1) % coords.length];

    if (isPointOnSegment(point, current, next)) {
      return true;
    }

    const currentAbove = current.y > point.y;
    const nextAbove = next.y > point.y;
    const intersects =
      currentAbove !== nextAbove &&
      point.x < ((next.x - current.x) * (point.y - current.y)) / (next.y - current.y) + current.x;

    if (intersects) {
      isInside = !isInside;
    }
  }

  return isInside;
};

export const getPolygonLabelPosition = (coords: Coordinates[]): Coordinates | undefined => {
  if (coords.length === 0) return undefined;

  const centroid = calcPolygonCentroid(coords);
  if (centroid && (coords.length < 3 || isPointInPolygon(centroid, coords))) {
    return centroid;
  }

  if (centroid && coords.length >= 3) {
    const intersections: number[] = [];
    const scanY = centroid.y;

    for (let index = 0; index < coords.length; index += 1) {
      const current = coords[index];
      const next = coords[(index + 1) % coords.length];

      if (current.y === next.y) continue;

      const lowerY = Math.min(current.y, next.y);
      const upperY = Math.max(current.y, next.y);
      if (scanY < lowerY || scanY >= upperY) continue;

      const x = current.x + ((scanY - current.y) * (next.x - current.x)) / (next.y - current.y);
      intersections.push(x);
    }

    intersections.sort((left, right) => left - right);

    let bestCandidate: Coordinates | undefined;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index + 1 < intersections.length; index += 2) {
      const left = intersections[index];
      const right = intersections[index + 1];
      if (right <= left) continue;

      const candidateX = (left + right) / 2;
      const distance = Math.abs(candidateX - centroid.x);

      if (distance < bestDistance) {
        bestDistance = distance;
        bestCandidate = { x: candidateX, y: scanY };
      }
    }

    if (bestCandidate && isPointInPolygon(bestCandidate, coords)) {
      return bestCandidate;
    }
  }

  const average = getAveragePoint(coords);
  if (coords.length < 3 || isPointInPolygon(average, coords)) {
    return average;
  }

  return coords[0];
};

const getAveragePoint = (coords: Coordinates[]) => {
  const average = coords.reduce((acc, coord) => ({ x: acc.x + coord.x, y: acc.y + coord.y }), {
    x: 0,
    y: 0,
  });

  return { x: average.x / coords.length, y: average.y / coords.length };
};

export const escapeHtml = (value: string) => {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
};

export const createPolygonLabelIcon = (label: string, color: string, isSelected: boolean) => {
  const fontWeight = isSelected ? "700" : "500";
  const border = `${isSelected ? 3 : 1}px solid ${color}`;
  const opacity = isSelected ? 1.0 : 0.7;
  return new DivIcon({
    html: `<div class="polygon-label" style="--polygon-label-color: ${color}; --opacity: ${opacity}; font-weight: ${fontWeight}; border: ${border}">${escapeHtml(label)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
};

export const createPinLabelIcon = (label: string, isSelected = false) => {
  const cacheKey = `${label}:${isSelected ? "1" : "0"}`;
  const cached = pinLabelIconCache.get(cacheKey);
  if (cached) return cached;

  const selectedClass = isSelected ? " is-selected" : "";
  const icon = new DivIcon({
    html: `<div class="pin-label${selectedClass}">${escapeHtml(label)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });

  pinLabelIconCache.set(cacheKey, icon);
  return icon;
};

const pinLabelIconCache = new Map<string, DivIcon>();
